"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import SecondaryButton from "../../Components/SecondaryButton";
import axios from "axios";

import {
  TbActivity,
  TbAlertCircle,
  TbBuilding,
  TbCheck,
  TbChevronDown,
  TbChevronUp,
  TbCircleCheck,
  TbClock,
  TbDeviceDesktop,
  TbFilter,
  TbInfoCircle,
  TbListDetails,
  TbRefresh,
  TbSearch,
  TbShield,
  TbUsers,
  TbWorld,
  TbX,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";

function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div
        className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}
      >
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
          type="button"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default function UserAccessLogs() {
  const API_ORIGIN = useMemo(() => {
    return process.env.REACT_APP_API_ORIGIN || "https://new.cajuu.app";
  }, []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [rangeFrom, setRangeFrom] = useState(0);
  const [rangeTo, setRangeTo] = useState(0);
  const [perPage, setPerPage] = useState(20);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");

  const [companySearch, setCompanySearch] = useState("");
  const [debouncedCompanySearch, setDebouncedCompanySearch] = useState("");

  const [eventFilter, setEventFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [browserNameFilter, setBrowserNameFilter] = useState("all");
  const [operatingSystemFilter, setOperatingSystemFilter] = useState("all");
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("all");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [sortBy, setSortBy] = useState("accessed_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const [expandedRows, setExpandedRows] = useState({});

  const [filterOptions, setFilterOptions] = useState({
    events: [],
    methods: [],
    browserNames: [],
    operatingSystems: [],
    deviceTypes: [],
  });

  const [kpis, setKpis] = useState({
    totalLogs: 0,
    uniqueUsers: 0,
    uniqueCompanies: 0,
    errorCount: 0,
    lastAccessedAt: null,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const getToken = () => localStorage.getItem("token");

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, message, type });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 450);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearch), 450);
    return () => clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCompanySearch(companySearch), 450);
    return () => clearTimeout(timer);
  }, [companySearch]);

  const parsePaginator = (apiPayload) => {
    if (
      apiPayload?.data &&
      typeof apiPayload.data === "object" &&
      Array.isArray(apiPayload.data.data)
    ) {
      return apiPayload.data;
    }

    return null;
  };

  const normalizeMetadata = (metadata) => {
    if (!metadata) return {};

    if (typeof metadata === "object") {
      return metadata;
    }

    if (typeof metadata === "string") {
      try {
        return JSON.parse(metadata);
      } catch (e) {
        return {};
      }
    }

    return {};
  };

  const userLabelFromApiUser = (user) => {
    if (!user) return "—";

    const first = user?.first_name || "";
    const last = user?.last_name || "";
    const full = `${first} ${last}`.trim();

    if (full) return full;
    if (user?.username) return user.username;
    if (user?.email) return user.email;

    return user?.id ? `User #${user.id}` : "—";
  };

  const companyLabelFromApiUser = (user) => {
    const company = user?.partner_company || user?.partnerCompany || null;

    if (!company) return "—";

    return company?.partner_company_name || company?.email || `Company #${company?.id}`;
  };

  const mapLogFromApi = (log) => {
    const user = log?.user || null;
    const company = user?.partner_company || user?.partnerCompany || null;
    const metadata = normalizeMetadata(log?.metadata);

    return {
      id: String(log?.id ?? ""),
      original_id: log?.id,
      user_id: log?.user_id ?? null,
      user_name: userLabelFromApiUser(user),
      user_email: user?.email || "",
      user_username: user?.username || "",
      user_phone: user?.phone || "",
      company_id: company?.id ?? null,
      company_name: companyLabelFromApiUser(user),
      company_dot: company?.dot || "",
      company_email: company?.email || "",
      event: log?.event || "—",
      method: log?.method || "—",
      path: log?.path || "—",
      url: log?.url || "",
      status_code: log?.status_code ?? null,
      ip_address: log?.ip_address || "—",
      user_agent: log?.user_agent || "",
      browser_name: log?.browser_name || "—",
      browser_version: log?.browser_version || "",
      operating_system: log?.operating_system || "—",
      operating_system_version: log?.operating_system_version || "",
      device_type: log?.device_type || "—",
      referer: log?.referer || "",
      request_id: log?.request_id || "",
      metadata,
      screen_name: metadata?.screen_name || "—",
      route_action: metadata?.route_action || "",
      host: metadata?.host || "",
      accept_language: metadata?.accept_language || "",
      ajax: metadata?.ajax ?? null,
      secure: metadata?.secure ?? null,
      accessed_at: log?.accessed_at || "",
      created_at: log?.created_at || "",
      original: log,
    };
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";

    const safeValue = String(dateString).replace(" ", "T");
    const date = new Date(safeValue);

    if (Number.isNaN(date.getTime())) return "—";

    const datePart = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const timePart = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${datePart} ${timePart}`;
  };

  const truncateText = (value, max = 80) => {
    const text = String(value || "");
    if (!text) return "—";
    if (text.length <= max) return text;
    return `${text.slice(0, max)}...`;
  };

  const statusBadge = (statusCode) => {
    const code = Number(statusCode || 0);

    if (code >= 200 && code < 300) {
      return "border-emerald-700 bg-emerald-900/20 text-emerald-200";
    }

    if (code >= 300 && code < 400) {
      return "border-blue-700 bg-blue-900/20 text-blue-200";
    }

    if (code >= 400 && code < 500) {
      return "border-amber-700 bg-amber-900/20 text-amber-200";
    }

    if (code >= 500) {
      return "border-red-700 bg-red-900/20 text-red-200";
    }

    return "border-zinc-700 bg-zinc-900/20 text-zinc-200";
  };

  const methodBadge = (method) => {
    const value = String(method || "").toUpperCase();

    if (value === "GET") return "border-blue-700 bg-blue-900/20 text-blue-200";
    if (value === "POST") return "border-emerald-700 bg-emerald-900/20 text-emerald-200";
    if (value === "PUT" || value === "PATCH") return "border-amber-700 bg-amber-900/20 text-amber-200";
    if (value === "DELETE") return "border-red-700 bg-red-900/20 text-red-200";

    return "border-zinc-700 bg-zinc-900/20 text-zinc-200";
  };

  const buildServerParams = (page) => {
    const params = {
      page: page || 1,
      per_page: perPage || 20,
      sort_by: sortBy || "accessed_at",
      sort_direction: sortDirection || "desc",
      with_kpis: 1,
    };

    const q = (debouncedSearchQuery || "").trim();
    if (q) params.q = q;

    const userQ = (debouncedUserSearch || "").trim();
    if (userQ) params.user_q = userQ;

    const companyQ = (debouncedCompanySearch || "").trim();
    if (companyQ) params.company_q = companyQ;

    if (eventFilter && eventFilter !== "all") params.event = eventFilter;
    if (methodFilter && methodFilter !== "all") params.method = methodFilter;
    if (browserNameFilter && browserNameFilter !== "all") params.browser_name = browserNameFilter;
    if (operatingSystemFilter && operatingSystemFilter !== "all") params.operating_system = operatingSystemFilter;
    if (deviceTypeFilter && deviceTypeFilter !== "all") params.device_type = deviceTypeFilter;

    if (dateFrom) params.date_from = `${dateFrom} 00:00:00`;
    if (dateTo) params.date_to = `${dateTo} 23:59:59`;

    return params;
  };

  const fetchFilterOptions = async () => {
    setOptionsLoading(true);

    try {
      const token = getToken();

      const res = await axios.get(`${API_ORIGIN}/api/user-access-logs/filter-options`, {
        headers: {
          "Screen-Name": "/UserAccessLogs",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = res?.data?.data || {};

      setFilterOptions({
        events: Array.isArray(data?.events) ? data.events : [],
        methods: Array.isArray(data?.methods) ? data.methods : [],
        browserNames: Array.isArray(data?.browserNames) ? data.browserNames : [],
        operatingSystems: Array.isArray(data?.operatingSystems) ? data.operatingSystems : [],
        deviceTypes: Array.isArray(data?.deviceTypes) ? data.deviceTypes : [],
      });
    } catch (e) {
      setFilterOptions({
        events: [],
        methods: [],
        browserNames: [],
        operatingSystems: [],
        deviceTypes: [],
      });
    } finally {
      setOptionsLoading(false);
    }
  };

  const fetchLogs = async (page = 1) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await axios.get(`${API_ORIGIN}/api/user-access-logs/paginated`, {
        headers: {
          "Screen-Name": "/UserAccessLogs",
          Authorization: `Bearer ${token}`,
        },
        params: buildServerParams(page),
      });

      const paginator = parsePaginator(res.data);
      const list = Array.isArray(paginator?.data) ? paginator.data : [];
      const mapped = list.map(mapLogFromApi);

      const cp = Number(paginator?.current_page ?? page) || 1;
      const lp = Number(paginator?.last_page ?? 1) || 1;
      const tt = Number(paginator?.total ?? mapped.length) || 0;
      const pp = Number(paginator?.per_page ?? perPage) || perPage;

      setItems(mapped);
      setCurrentPage(cp);
      setTotalPages(lp);
      setTotalItems(tt);
      setPerPage(pp);
      setRangeFrom(Number(paginator?.from ?? (mapped.length ? 1 : 0)) || 0);
      setRangeTo(Number(paginator?.to ?? mapped.length) || 0);

      const serverKpis = paginator?.kpis;
      if (serverKpis && typeof serverKpis === "object") {
        setKpis({
          totalLogs: Number(serverKpis.totalLogs ?? 0),
          uniqueUsers: Number(serverKpis.uniqueUsers ?? 0),
          uniqueCompanies: Number(serverKpis.uniqueCompanies ?? 0),
          errorCount: Number(serverKpis.errorCount ?? 0),
          lastAccessedAt: serverKpis.lastAccessedAt ?? null,
        });
      } else {
        setKpis({
          totalLogs: mapped.length,
          uniqueUsers: new Set(mapped.map((x) => x.user_id).filter(Boolean)).size,
          uniqueCompanies: new Set(mapped.map((x) => x.company_id).filter(Boolean)).size,
          errorCount: mapped.filter((x) => Number(x.status_code || 0) >= 400).length,
          lastAccessedAt: mapped[0]?.accessed_at || null,
        });
      }
    } catch (e) {
      setItems([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalItems(0);
      setRangeFrom(0);
      setRangeTo(0);
      setKpis({
        totalLogs: 0,
        uniqueUsers: 0,
        uniqueCompanies: 0,
        errorCount: 0,
        lastAccessedAt: null,
      });

      const message =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Erro ao carregar logs de acesso";
      showSnackbar(message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLogs(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    perPage,
    sortBy,
    sortDirection,
    debouncedSearchQuery,
    debouncedUserSearch,
    debouncedCompanySearch,
    eventFilter,
    methodFilter,
    browserNameFilter,
    operatingSystemFilter,
    deviceTypeFilter,
    dateFrom,
    dateTo,
  ]);

  const clearFilters = () => {
    setSearchQuery("");
    setUserSearch("");
    setCompanySearch("");
    setEventFilter("all");
    setMethodFilter("all");
    setBrowserNameFilter("all");
    setOperatingSystemFilter("all");
    setDeviceTypeFilter("all");
    setDateFrom("");
    setDateTo("");
    setSortBy("accessed_at");
    setSortDirection("desc");
    setPerPage(20);
    setCurrentPage(1);
    setExpandedRows({});
    showSnackbar("Filters cleared", "info");
  };

  const goToPage = (pageNumber) => {
    const target = Math.max(1, Math.min(Number(pageNumber) || 1, totalPages || 1));
    if (target === currentPage) return;
    setCurrentPage(target);
  };

  const nextPage = () => {
    if (currentPage >= totalPages) return;
    goToPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage <= 1) return;
    goToPage(currentPage - 1);
  };

  const toggleExpandedRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderOptionList = (itemsList, fallbackList = []) => {
    const source = Array.isArray(itemsList) && itemsList.length > 0 ? itemsList : fallbackList;

    return source.map((item) => (
      <option key={item} value={item} className="bg-[#1c1b19]">
        {item}
      </option>
    ));
  };

  const methodFallbackOptions = ["GET", "POST", "PUT", "PATCH", "DELETE"];
  const deviceFallbackOptions = ["desktop", "mobile", "tablet", "bot", "unknown"];

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection
            title="USER ACCESS LOGS"
            description="SuperAdmin view for access logs across all users and partner companies"
          />
        </GroupedContainer>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-gray-400 uppercase tracking-wider">Logs (filtered)</div>
              <TbActivity className="w-5 h-5 text-amber-400" />
            </div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-white">
              {Number(kpis.totalLogs || 0)}
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-gray-400 uppercase tracking-wider">Users (filtered)</div>
              <TbUsers className="w-5 h-5 text-blue-300" />
            </div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-blue-200">
              {Number(kpis.uniqueUsers || 0)}
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-gray-400 uppercase tracking-wider">Companies (filtered)</div>
              <TbBuilding className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-emerald-200">
              {Number(kpis.uniqueCompanies || 0)}
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-gray-400 uppercase tracking-wider">Errors (4xx/5xx)</div>
              <TbAlertCircle className="w-5 h-5 text-red-300" />
            </div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-red-200">
              {Number(kpis.errorCount || 0)}
            </div>
            <div className="mt-1 text-xs text-white/50">
              Last: {formatDateTime(kpis.lastAccessedAt)}
            </div>
          </div>
        </div>

        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <TbFilter className="text-amber-500 w-5 h-5" />
              Filters
            </h2>

            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchFilterOptions()}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
                type="button"
                disabled={optionsLoading}
              >
                <TbRefresh className={`w-4 h-4 ${optionsLoading ? "animate-spin" : ""}`} />
                Refresh options
              </button>

              <button
                onClick={clearFilters}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
                type="button"
              >
                <TbX className="w-4 h-4" /> Clear Filters
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative lg:col-span-2">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbSearch className="text-white text-lg" />
              </div>
              <input
                type="text"
                placeholder="Universal search: user, event, method, path, URL, IP..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  type="button"
                >
                  <TbX className="text-white" />
                </button>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbUsers className="text-white text-lg" />
              </div>
              <input
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              {userSearch && (
                <button
                  onClick={() => {
                    setUserSearch("");
                    setCurrentPage(1);
                  }}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  type="button"
                >
                  <TbX className="text-white" />
                </button>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbBuilding className="text-white text-lg" />
              </div>
              <input
                type="text"
                placeholder="Search company, DOT or email..."
                value={companySearch}
                onChange={(e) => {
                  setCompanySearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              {companySearch && (
                <button
                  onClick={() => {
                    setCompanySearch("");
                    setCurrentPage(1);
                  }}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  type="button"
                >
                  <TbX className="text-white" />
                </button>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbActivity className="text-amber-500 text-lg" />
              </div>
              <select
                value={eventFilter}
                onChange={(e) => {
                  setEventFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="all" className="bg-[#1c1b19]">All Events</option>
                {renderOptionList(filterOptions.events)}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbWorld className="text-amber-500 text-lg" />
              </div>
              <select
                value={methodFilter}
                onChange={(e) => {
                  setMethodFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="all" className="bg-[#1c1b19]">All Methods</option>
                {renderOptionList(filterOptions.methods, methodFallbackOptions)}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbWorld className="text-gray-400 text-lg" />
              </div>
              <select
                value={browserNameFilter}
                onChange={(e) => {
                  setBrowserNameFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="all" className="bg-[#1c1b19]">All Browsers</option>
                {renderOptionList(filterOptions.browserNames)}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbDeviceDesktop className="text-gray-400 text-lg" />
              </div>
              <select
                value={operatingSystemFilter}
                onChange={(e) => {
                  setOperatingSystemFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="all" className="bg-[#1c1b19]">All Operation Systems</option>
                {renderOptionList(filterOptions.operatingSystems)}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbDeviceDesktop className="text-gray-400 text-lg" />
              </div>
              <select
                value={deviceTypeFilter}
                onChange={(e) => {
                  setDeviceTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="all" className="bg-[#1c1b19]">All Device Types</option>
                {renderOptionList(filterOptions.deviceTypes, deviceFallbackOptions)}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="md:col-span-2 lg:col-span-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="relative">
                  <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">Accessed From</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    onFocus={(e) => e.target.showPicker?.()}
                    style={{ colorScheme: "dark" }}
                    className="w-full bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="relative">
                  <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">Accessed To</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    onFocus={(e) => e.target.showPicker?.()}
                    style={{ colorScheme: "dark" }}
                    className="w-full bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
              <select
                value={String(perPage)}
                onChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="10" className="bg-[#1c1b19]">10 / page</option>
                <option value="20" className="bg-[#1c1b19]">20 / page</option>
                <option value="50" className="bg-[#1c1b19]">50 / page</option>
                <option value="100" className="bg-[#1c1b19]">100 / page</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbFilter className="text-amber-500 text-lg" />
              </div>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="accessed_at" className="bg-[#1c1b19]">Order by: Accessed At</option>
                <option value="event" className="bg-[#1c1b19]">Order by: Event</option>
                <option value="method" className="bg-[#1c1b19]">Order by: Method</option>
                <option value="status_code" className="bg-[#1c1b19]">Order by: Status Code</option>
                <option value="ip_address" className="bg-[#1c1b19]">Order by: IP</option>
                <option value="browser_name" className="bg-[#1c1b19]">Order by: Browser</option>
                <option value="operating_system" className="bg-[#1c1b19]">Order by: OS</option>
                <option value="device_type" className="bg-[#1c1b19]">Order by: Device</option>
                <option value="user_name" className="bg-[#1c1b19]">Order by: User</option>
                <option value="partner_company_name" className="bg-[#1c1b19]">Order by: Company</option>
                <option value="id" className="bg-[#1c1b19]">Order by: ID</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
              <select
                value={sortDirection}
                onChange={(e) => {
                  setSortDirection(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="asc" className="bg-[#1c1b19]">Direction: ASC</option>
                <option value="desc" className="bg-[#1c1b19]">Direction: DESC</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>
          </div>

          <div className="mt-3 text-xs text-white/70">
            Server pagination: page {currentPage} of {totalPages} • total {totalItems} • per page {perPage}
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-600">
          <table className="min-w-full divide-y divide-zinc-600">
            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Accessed At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Event</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Screen / Path</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">IP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Device</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Details</th>
              </tr>
            </thead>

            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    </div>
                    <p className="mt-2 text-gray-400">Loading user access logs...</p>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((row) => {
                  const isExpanded = !!expandedRows[row.id];

                  return (
                    <Fragment key={row.id}>
                      <tr className="hover:bg-zinc-800/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">#{row.id}</td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <div className="flex items-center gap-2">
                            <TbClock className="w-4 h-4 text-white/50" />
                            {formatDateTime(row.accessed_at)}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <div className="leading-tight max-w-[220px]">
                            <div className="font-semibold text-white truncate" title={row.company_name}>
                              {row.company_name}
                            </div>
                            <div className="text-[11px] text-white/50 truncate" title={row.company_email || row.company_dot}>
                              {row.company_dot ? `DOT ${row.company_dot}` : row.company_email || "—"}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <div className="leading-tight max-w-[240px]">
                            <div className="font-semibold text-white truncate" title={row.user_name}>
                              {row.user_name}
                            </div>
                            <div className="text-[11px] text-white/50 truncate" title={row.user_email || row.user_username || row.user_phone}>
                              {row.user_email || row.user_username || row.user_phone || "—"}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <span className="inline-flex items-center px-3 py-1 rounded-full border border-zinc-700 bg-zinc-900/20 text-xs text-zinc-100">
                            {row.event}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs ${methodBadge(row.method)}`}>
                            {String(row.method || "—").toUpperCase()}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs ${statusBadge(row.status_code)}`}>
                            {row.status_code || "—"}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-sm text-gray-300">
                          <div className="leading-tight max-w-[320px]">
                            <div className="font-semibold text-white truncate" title={row.screen_name}>
                              {row.screen_name}
                            </div>
                            <div className="text-[11px] text-white/50 truncate" title={row.path}>
                              {row.path}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {row.ip_address}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <div className="leading-tight max-w-[220px]">
                            <div className="font-semibold text-white truncate" title={`${row.browser_name} ${row.browser_version}`}>
                              {row.browser_name}{row.browser_version ? ` ${row.browser_version}` : ""}
                            </div>
                            <div className="text-[11px] text-white/50 truncate" title={`${row.operating_system} ${row.operating_system_version} / ${row.device_type}`}>
                              {row.operating_system}{row.operating_system_version ? ` ${row.operating_system_version}` : ""} • {row.device_type}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleExpandedRow(row.id)}
                            className="px-3 py-2 text-xs rounded-lg border border-zinc-600 text-white hover:bg-zinc-800/50 transition-colors flex items-center gap-2"
                            type="button"
                          >
                            <TbListDetails className="w-4 h-4" />
                            {isExpanded ? "Hide" : "Details"}
                            {isExpanded ? <TbChevronUp className="w-4 h-4" /> : <TbChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr key={`${row.id}-details`} className="bg-zinc-950/30">
                          <td colSpan="11" className="px-6 py-5">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                              <div className="bg-black/20 border border-zinc-700 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-white font-semibold mb-3">
                                  <TbShield className="w-4 h-4 text-amber-400" />
                                  Request
                                </div>
                                <div className="space-y-2 text-xs text-white/70">
                                  <div>
                                    <span className="text-white/40">URL:</span>{" "}
                                    <span title={row.url}>{truncateText(row.url, 120)}</span>
                                  </div>
                                  <div>
                                    <span className="text-white/40">Referer:</span>{" "}
                                    <span title={row.referer}>{truncateText(row.referer, 120)}</span>
                                  </div>
                                  <div>
                                    <span className="text-white/40">Request ID:</span>{" "}
                                    <span>{row.request_id || "—"}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-black/20 border border-zinc-700 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-white font-semibold mb-3">
                                  <TbWorld className="w-4 h-4 text-blue-300" />
                                  Metadata
                                </div>
                                <div className="space-y-2 text-xs text-white/70">
                                  <div>
                                    <span className="text-white/40">Host:</span>{" "}
                                    <span>{row.host || "—"}</span>
                                  </div>
                                  <div>
                                    <span className="text-white/40">Screen:</span>{" "}
                                    <span>{row.screen_name}</span>
                                  </div>
                                  <div>
                                    <span className="text-white/40">Route Action:</span>{" "}
                                    <span title={row.route_action}>{truncateText(row.route_action, 120)}</span>
                                  </div>
                                  <div>
                                    <span className="text-white/40">AJAX:</span>{" "}
                                    <span>{row.ajax === null ? "—" : String(row.ajax)}</span>
                                  </div>
                                  <div>
                                    <span className="text-white/40">Secure:</span>{" "}
                                    <span>{row.secure === null ? "—" : String(row.secure)}</span>
                                  </div>
                                  <div>
                                    <span className="text-white/40">Accept Language:</span>{" "}
                                    <span>{row.accept_language || "—"}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-black/20 border border-zinc-700 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-white font-semibold mb-3">
                                  <TbDeviceDesktop className="w-4 h-4 text-emerald-300" />
                                  User Agent
                                </div>
                                <div className="text-xs text-white/70 break-words leading-relaxed">
                                  {row.user_agent || "—"}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center text-gray-400">
                    <div className="text-gray-400">
                      <TbInfoCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      No user access logs found matching your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1 || loading}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => goToPage(i)}
                        disabled={loading}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }
                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages || loading}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {rangeFrom}-{rangeTo} of {totalItems} access logs
            </div>
          </>
        )}
      </div>

      {snackbar.open && (
        <Snackbar message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} duration={5000} />
      )}
    </AuthenticatedLayout>
  );
}
